// src/components/DynamicInputs/SelectableOptionWithOther.jsx
import React, { useState, useEffect } from 'react';
import SelectableOption from '../FormElements/SelectableOption.jsx';
import TextInput from '../FormElements/TextInput.jsx';

const SelectableOptionWithOther = ({ schema, value: currentCompositeValue, onChange: driverOnChange }) => {
    // Defensive: ensure configs are always objects
    const {
        options = [],
        initialValue: schemaInitialValue = { selections: [], otherDetail: '' },
        componentProps = {},
        otherInputConfig: rawOtherInputConfig,
        noneOfTheAboveConfig: rawNoneOfTheAboveConfig,
        validation: schemaValidation,
        errorMessages: schemaErrorMessages,
        layout = {},
        id: questionId,
    } = schema;

    const otherInputConfig = rawOtherInputConfig || {};
    const noneOfTheAboveConfig = rawNoneOfTheAboveConfig || {};

    const isMultiSelect = componentProps?.isMultiSelect || false;
    const {
        optionValue: otherOptionValue = "other",
        textInputPlaceholder,
        textInputLabel,
        textInputWrapperClassName,
        placeholder: legacyOtherPlaceholder
    } = otherInputConfig;
    const otherPlaceholder = textInputPlaceholder || legacyOtherPlaceholder || "Please specify";

    const validation = schemaValidation || {};
    const errorMessages = schemaErrorMessages || {};

    const initialSelectionsKey = Object.keys(schemaInitialValue).find(k => Array.isArray(schemaInitialValue[k])) || 'selections';
    const initialOtherDetailKey = Object.keys(schemaInitialValue).find(k => typeof schemaInitialValue[k] === 'string' && k !== initialSelectionsKey) || 'otherDetail';

    const safeInitialValue = { [initialSelectionsKey]: [], [initialOtherDetailKey]: '', ...schemaInitialValue };
    const currentVal = currentCompositeValue || safeInitialValue;

    const currentSelections = currentVal[initialSelectionsKey] || [];
    const currentOtherDetail = currentVal[initialOtherDetailKey] || '';

    // None of the Above config
    const {
        optionValue: noneOfTheAboveValue = "noneOfTheAbove",
        optionLabel: noneOfTheAboveLabel = "Ninguna de las anteriores",
        show: showNoneOfTheAbove = Object.keys(noneOfTheAboveConfig).length > 0
    } = noneOfTheAboveConfig || {};

    const hasNoneOfTheAboveSelected = showNoneOfTheAbove && currentSelections.includes(noneOfTheAboveValue);

    // If "None of the above" is selected, all other options (except "None of the above" itself) are disabled
    const isOptionDisabled = (optValue) => {
        if (!showNoneOfTheAbove) return false;
        if (optValue === noneOfTheAboveValue) return false;
        // Disable all other options if "None of the Above" is selected
        if (hasNoneOfTheAboveSelected) return true;
        return false;
    };

    const [internalError, setInternalError] = useState('');

    const hasOtherSelected = currentSelections.includes(otherOptionValue);

    useEffect(() => {
        // Clear error if 'Other' is deselected or if it's selected but 'otherInputRequired' validation is not active.
        if (!hasOtherSelected && internalError) {
            setInternalError('');
        }
        // If 'Other' is selected and otherDetail is empty, re-validate
        if (hasOtherSelected && currentOtherDetail.trim() === '' && validation.otherInputRequired) {
            validateAndReport(currentSelections, currentOtherDetail, true); // Re-validate without forcing error if not required
        } else if (hasOtherSelected && currentOtherDetail.trim() !== '') {
            setInternalError(''); // Clear error if 'Other' is selected and has text
        }
    }, [hasOtherSelected, currentOtherDetail, internalError, validation.otherInputRequired]);

    const validateAndReport = (newSelections, newOtherDetail, isInitialCheck = false) => {
        let error = '';
        const finalOtherDetail = hasOtherSelected ? newOtherDetail : ''; // Ensure otherDetail is cleared if 'other' is not selected

        if (validation.required) {
            if (newSelections.length === 0) {
                error = errorMessages.required || "Selection required.";
            }
        }
        if (!error && validation.minSelections && newSelections.length < validation.minSelections) {
            error = errorMessages.minSelections || `Please select at least ${validation.minSelections} option(s).`;
        }
        if (!error && validation.maxSelections && newSelections.length > validation.maxSelections) {
            error = errorMessages.maxSelections || `Please select no more than ${validation.maxSelections} option(s).`;
        }

        if (!error && hasOtherSelected && validation.otherInputRequired && finalOtherDetail.trim() === '') {
            error = errorMessages.otherInputRequired || "Please specify details for 'Other'.";
        }
        if (!error && hasOtherSelected && finalOtherDetail.trim() !== '' && validation.otherInputMinLength && finalOtherDetail.length < validation.otherInputMinLength) {
            error = errorMessages.otherInputMinLength || `Other details must be at least ${validation.otherInputMinLength} characters.`;
        }
        if (!error && hasOtherSelected && finalOtherDetail.trim() !== '' && validation.otherInputMaxLength && finalOtherDetail.length > validation.otherInputMaxLength) {
            error = errorMessages.otherInputMaxLength || `Other details must not exceed ${validation.otherInputMaxLength} characters.`;
        }


        setInternalError(error);
        driverOnChange(
            { [initialSelectionsKey]: newSelections, [initialOtherDetailKey]: finalOtherDetail },
            error
        );
    };

    const handleOptionClick = (clickedOptionValue) => {
        let newSelections;
        if (isMultiSelect) {
            if (clickedOptionValue === noneOfTheAboveValue) {
                // Only "None of the above" can be selected, uncheck all others including "Other"
                if (currentSelections.includes(noneOfTheAboveValue)) {
                    newSelections = currentSelections.filter(v => v !== noneOfTheAboveValue);
                } else {
                    newSelections = [noneOfTheAboveValue];
                }
            } else {
                // If any other option is checked, remove "None of the above"
                newSelections = currentSelections.filter(v => v !== noneOfTheAboveValue);
                if (currentSelections.includes(clickedOptionValue)) {
                    newSelections = newSelections.filter(v => v !== clickedOptionValue);
                } else {
                    newSelections = [...newSelections, clickedOptionValue];
                }
            }
        } else {
            newSelections = clickedOptionValue === currentSelections[0] && !hasOtherSelected ? [] : [clickedOptionValue];
            // If it's single select and 'Other' is chosen, clear previous selections
            if (clickedOptionValue === otherOptionValue) {
                newSelections = [otherOptionValue];
            }
        }
        validateAndReport(newSelections, currentOtherDetail);
    };

    const handleOtherInputChange = (event) => {
        const newOtherDetail = event.target.value;
        validateAndReport(currentSelections, newOtherDetail);
    };

    const answerLayout = layout?.answerLayout || {};

    // Support both flex and grid layouts
    let containerStyle = {};
    if (answerLayout.type === 'flex') {
        containerStyle = {
            display: 'flex',
            flexDirection: answerLayout.direction || 'row',
            flexWrap: answerLayout.wrap || 'wrap',
            gap: answerLayout.gap
                ? /^\d+$/.test(answerLayout.gap)
                    ? `${parseFloat(answerLayout.gap) * 0.25}rem`
                    : answerLayout.gap
                : '0.5rem',
            justifyContent: answerLayout.justifyContent || 'flex-start',
            alignItems: answerLayout.alignItems || 'stretch',
        };
    } else if (answerLayout.type === 'grid') {
        // Support columnsPerSection and gap
        const columns = answerLayout.columnsPerSection || 2;
        let gapValue = '0.5rem';
        if (answerLayout.gap) {
            if (typeof answerLayout.gap === 'string' && answerLayout.gap.includes(' ')) {
                // Support two values for row/column gap, e.g., "1 3"
                const [rowGap, colGap] = answerLayout.gap.split(' ').map(v => {
                    if (/^\d+$/.test(v)) return `${parseFloat(v) * 0.25}rem`;
                    return v;
                });
                gapValue = `${rowGap} ${colGap}`;
            } else if (/^\d+$/.test(answerLayout.gap)) {
                gapValue = `${parseFloat(answerLayout.gap) * 0.25}rem`;
            } else if (typeof answerLayout.gap === 'string') {
                gapValue = answerLayout.gap;
            }
        }
        containerStyle = {
            display: 'grid',
            gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
            gap: gapValue,
            alignItems: answerLayout.alignItems || 'stretch',
            justifyItems: answerLayout.justifyItems || 'stretch',
        };
    }

    return (
        <div
            className={layout?.answerOptionsContainerClassName || "mt-3"}
            style={containerStyle}
        >
            {options.map(opt => (
                <SelectableOption
                    key={opt.value}
                    option={opt}
                    isSelected={currentSelections.includes(opt.value)}
                    onClick={() => handleOptionClick(opt.value)}
                    className={componentProps?.optionClassName}
                    disabled={isOptionDisabled(opt.value) || currentSelections.includes(noneOfTheAboveValue)}
                />
            ))}
            {/* Always render the "Other" option if otherInputConfig is present and not already in options */}
            {Object.keys(otherInputConfig).length > 0 && otherInputConfig.optionValue && !options.some(opt => opt.value === otherOptionValue) && (
                <SelectableOption
                    key="other-checkbox"
                    option={{
                        value: otherOptionValue,
                        label: otherInputConfig.optionLabel || "Otro",
                        text: otherInputConfig.optionLabel || "Otro",
                    }}
                    isSelected={currentSelections.includes(otherOptionValue)}
                    onClick={() => handleOptionClick(otherOptionValue)}
                    className={componentProps?.optionClassName}
                    disabled={isOptionDisabled(otherOptionValue)}
                />
            )}
            {/* Render "None of the above" option if config is present and not already in options, always at the end */}
            {showNoneOfTheAbove && !options.some(opt => opt.value === noneOfTheAboveValue) && (
                <SelectableOption
                    key="none-of-the-above-checkbox"
                    option={{
                        value: noneOfTheAboveValue,
                        label: noneOfTheAboveLabel,
                        text: noneOfTheAboveLabel,
                        icon: noneOfTheAboveConfig.optionIcon || undefined,
                    }}
                    isSelected={currentSelections.includes(noneOfTheAboveValue)}
                    onClick={() => handleOptionClick(noneOfTheAboveValue)}
                    className={componentProps?.optionClassName}
                />
            )}
            {Object.keys(otherInputConfig).length > 0 && hasOtherSelected && (
                <div className={textInputWrapperClassName ? textInputWrapperClassName + " mt-4" : "mt-4"}>
                    {textInputLabel && (
                        <label htmlFor={`${questionId}-${initialOtherDetailKey}`} className="form-label">
                            {textInputLabel}
                        </label>
                    )}
                    <TextInput
                        id={`${questionId}-${initialOtherDetailKey}`}
                        name={`${questionId}-${initialOtherDetailKey}`}
                        type="text"
                        value={currentOtherDetail}
                        onChange={handleOtherInputChange}
                        placeholder={otherPlaceholder}
                        inputClassName={componentProps?.otherInputClassName || "form-control"}
                        ariaLabel={textInputLabel || "Other specification"}
                        noWrapper={true}
                    />
                    {internalError && validation.otherInputRequired && (
                        <div className="text-danger mt-1 ms-1" style={{ fontSize: '0.875em' }}>
                            {textInputLabel
                                ? `Obligatorio`
                                : internalError
                            }
                        </div>
                    )}
                </div>
            )}
            {!hasOtherSelected && internalError && ( // Show general error if no "Other" selected but there's a validation error (e.g., required)
                <div className="text-danger mt-1 ms-1" style={{ fontSize: '0.875em' }}>
                    {internalError}
                </div>
            )}
        </div>
    );
};

export default SelectableOptionWithOther;